import { useState } from "react";
import { bulkPutCards, getAllPacks } from "../db";
import type { Card } from "../domain/types";
import type { CardType, Phase, ModeId } from "../domain/enums";
import { Button } from "../components/Button";

/**
 * Admin CSV import (browser-side).
 * Mirrors scripts/import-csv.ts but runs in the page so the host can
 * paste/upload a CSV without a Node toolchain.
 *
 * Columns: mode,type,text_uk,text_ru,text_en[,author,comment]
 */
export default function AdminImport() {
  const [csvText, setCsvText] = useState("");
  const [report, setReport] = useState<string | null>(null);

  async function runImport() {
    setReport(null);
    const rows = parseCSV(csvText);
    if (rows.length === 0) {
      setReport("Empty CSV.");
      return;
    }
    const packs = await getAllPacks();
    const packByMode = new Map(packs.map((p) => [p.modeId, p]));

    // Distribute phases per row order: 33% warmup, 37% engagement, 30% peak.
    const total = rows.length;
    const cards: Card[] = [];
    let okRows = 0;
    rows.forEach((r, i) => {
      const modeId = r.mode as ModeId;
      const pack = packByMode.get(modeId);
      if (!pack) return;
      const type = r.type as CardType;
      const fraction = total > 0 ? i / total : 0;
      const phase: Phase = fraction < 0.34 ? "warmup" : fraction < 0.7 ? "engagement" : "peak";
      const intensity = phase === "warmup" ? 2 : phase === "engagement" ? 3 : 4;
      const energy = Math.min(5, intensity + (type === "task" || type === "mini_challenge" ? 1 : 0));
      const baseId = `csv_${i}_${Math.random().toString(36).slice(2, 7)}`;
      const rawPart = (r["participants"] || "").trim().toLowerCase();
      const participantsCount: number | "all" | null =
        rawPart === ""
          ? null
          : rawPart === "all"
            ? "all"
            : Number.isFinite(Number(rawPart))
              ? Math.max(0, Math.min(20, Number(rawPart)))
              : null;
      const rawKind = (r["kind"] || "main").trim().toLowerCase();
      const kind: "main" | "reward" | "penalty" =
        rawKind === "reward" || rawKind === "penalty" ? rawKind : "main";
      const triggersConsequence = (r["triggers"] || "").trim().toLowerCase() === "true";
      const VALID_INTERACTION = new Set([
        "self",
        "ask",
        "shout",
        "vote_room",
        "compete",
        "chain",
        "mirror",
        "solo",
      ]);
      const rawInter = (r["interaction"] || "").trim().toLowerCase();
      const interactionMode = VALID_INTERACTION.has(rawInter)
        ? (rawInter as Card["interactionMode"])
        : null;
      const parseSec = (raw: string, lo: number, hi: number): number | null => {
        const t = (raw || "").trim();
        if (!t) return null;
        const n = Number(t);
        return Number.isFinite(n) ? Math.max(lo, Math.min(hi, n)) : null;
      };
      const answerTimeSec = parseSec(r["answer_sec"] || "", 5, 180);
      const reactionWindowSec = parseSec(r["reaction_sec"] || "", 2, 15);
      (["uk", "ru", "en"] as const).forEach((lang) => {
        const text = (r[`text_${lang}` as keyof typeof r] || "").trim();
        if (!text) return;
        cards.push({
          id: `${lang}:${baseId}`,
          packId: pack.id,
          modeId,
          language: lang,
          type,
          phase,
          text,
          intensity: intensity as Card["intensity"],
          energy: energy as Card["energy"],
          socialBoldness: intensity as Card["socialBoldness"],
          durationSec: type === "mini_challenge" ? 30 : null,
          hasTimer: type === "mini_challenge",
          involvesOthers:
            type === "vote" ||
            type === "pair" ||
            type === "group" ||
            participantsCount === "all" ||
            (typeof participantsCount === "number" && participantsCount > 0),
          ageGate: pack.isAdult,
          participantsCount,
          interactionMode,
          answerTimeSec,
          reactionWindowSec,
          kind,
          triggersConsequence: kind === "main" ? triggersConsequence : false,
        });
      });
      okRows++;
    });
    await bulkPutCards(cards);
    setReport(`Imported ${okRows} rows → ${cards.length} card records (UK/RU/EN where present).`);
  }

  return (
    <div className="flex flex-col gap-3 pb-6">
      <h3 className="font-display text-lg font-bold">Import CSV</h3>
      <p className="text-xs opacity-70">
        Columns: <code>mode,type,text_uk,text_ru,text_en[,participants,interaction,answer_sec,reaction_sec,kind,triggers]</code> · phase
        auto-distributed (33/37/30).
      </p>
      <input
        type="file"
        accept=".csv,text/csv"
        className="text-xs"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          setCsvText(await file.text());
        }}
      />
      <textarea
        rows={10}
        className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2 font-mono text-xs"
        placeholder="mode,type,text_uk,text_ru,text_en"
        value={csvText}
        onChange={(e) => setCsvText(e.target.value)}
      />
      <Button onClick={runImport} disabled={!csvText.trim()}>
        Import
      </Button>
      {report && (
        <div className="text-sm rounded-lg bg-emerald-500/15 border border-emerald-400/30 p-3">
          {report}
        </div>
      )}
    </div>
  );
}

/** Tiny CSV parser — handles quoted fields with embedded commas/newlines. */
function parseCSV(input: string): Record<string, string>[] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < input.length; i++) {
    const ch = input[i] as string;
    if (inQuotes) {
      if (ch === '"' && input[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cur += ch;
      }
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ",") {
        row.push(cur);
        cur = "";
      } else if (ch === "\n") {
        row.push(cur);
        rows.push(row);
        row = [];
        cur = "";
      } else if (ch === "\r") {
        // ignore
      } else cur += ch;
    }
  }
  if (cur.length > 0 || row.length > 0) {
    row.push(cur);
    rows.push(row);
  }
  if (rows.length === 0) return [];
  const header = (rows[0] ?? []).map((h) => h.trim());
  return rows.slice(1).map((r) => {
    const obj: Record<string, string> = {};
    header.forEach((h, i) => (obj[h] = (r[i] ?? "").trim()));
    return obj;
  });
}
